import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Pill, 
  Search, 
  Trash2, 
  Edit,
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar,
  Truck,
  Shield,
  ScanBarcode
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { medicationsAPI, suppliersAPI } from '../lib/api';
import { formatCurrency, formatDate, formatDateTime, formatStock } from '../lib/utils';
import { toast } from 'sonner';
import { useSuperUser } from '../context/SuperUserContext';
import { useCanSeeAmounts } from '../context/PermissionsContext';
import { SuperUserDialog } from '../components/SuperUserDialog';
import AddMedicationWizard from '../components/medications/AddMedicationWizard';
import { MedicationMovementsSheet } from '../components/medications/MedicationMovementsSheet';
import { InventoryModernTableView } from '../components/medications/MedicationsInventoryTable';
import { MEDICATION_CATEGORIES, MEDICATION_PRESENTATIONS, MEDICATION_UNITS, ADMINISTRATION_TYPES } from '../components/medications/medicationConstants';

export default function MedicationsPage() {
  const [medications, setMedications] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMedication, setEditingMedication] = useState(null);
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [searchingBarcode, setSearchingBarcode] = useState(false);
  
  // Super User states
  const { isSuperUser } = useSuperUser();
  const canSeeMedAmounts = useCanSeeAmounts('medications');
  const [superUserDialogOpen, setSuperUserDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Wizard dialog (nuevo medicamento / agregar stock)
  const [wizardOpen, setWizardOpen] = useState(false);

  // TEMPORAL: selector de vista para elegir nuevo diseño del inventario
  // Vista de inventario consolidada (Opción B: Tabla Moderna)
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    presentation: '',
    presentation_quantity: 0,
    category: '',
    administration_type: '',
    initial_quantity: 0,
    min_stock: 0,
    unit: '',
    currency: '',
    supplier_id: '',
    purchase_price: "",
    barcode: '',
    lot_number: '',
    expiration_date: '',
    entry_date: ''
  });

  // Movement form state
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementForm, setMovementForm] = useState({
    medication_id: '',
    type: 'entrada',
    quantity: 0,
    unit_cost: 0,
    currency: 'DOP',
    supplier_id: '',
    entry_date: '',
    notes: ''
  });

  // New supplier dialog
  const [newSupplierDialogOpen, setNewSupplierDialogOpen] = useState(false);
  const [newSupplierForm, setNewSupplierForm] = useState({
    name: '', rnc: '', phone: '', email: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [medsRes, movesRes, suppliersRes] = await Promise.all([
        medicationsAPI.getAll(),
        medicationsAPI.getMovements(),
        suppliersAPI.getAll(),
      ]);
      setMedications(medsRes.data);
      setMovements(movesRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (!newSupplierForm.name.trim()) {
      toast.error('El nombre del proveedor es requerido');
      return;
    }
    try {
      const res = await suppliersAPI.create(newSupplierForm);
      toast.success('Proveedor creado');
      setSuppliers([...suppliers, res.data]);
      setFormData({...formData, supplier_id: res.data.id});
      setNewSupplierDialogOpen(false);
      setNewSupplierForm({ name: '', rnc: '', phone: '', email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proveedor');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      presentation: '',
      presentation_quantity: 0,
      category: '',
      administration_type: '',
      initial_quantity: 0,
      min_stock: 0,
      unit: '',
      currency: '',
      supplier_id: '',
      purchase_price: "",
      barcode: '',
      lot_number: '',
      expiration_date: '',
      entry_date: ''
    });
    setEditingMedication(null);
    setSelectedExisting(null);
    setNameSuggestions([]);
    setShowSuggestions(false);
    setBarcodeSearch('');
  };

  // Buscar por código de barras
  const handleBarcodeSearch = async (barcode) => {
    setBarcodeSearch(barcode);
    if (barcode.length < 3) return;
    
    setSearchingBarcode(true);
    try {
      const response = await medicationsAPI.getByBarcode(barcode);
      if (response.data) {
        // Medicamento encontrado - seleccionarlo
        selectExistingMedication(response.data, true); // true = permitir cambiar precio
        toast.success(`Medicamento encontrado: ${response.data.name}`);
      }
    } catch (error) {
      // No encontrado - es un código nuevo
      if (error.response?.status === 404) {
        setFormData(prev => ({...prev, barcode: barcode}));
      }
    } finally {
      setSearchingBarcode(false);
    }
  };

  // Buscar sugerencias mientras escribe el nombre
  const handleNameChange = (value) => {
    setFormData({...formData, name: value});
    
    // Mostrar sugerencias desde el primer caracter
    if (value.length >= 1 && !editingMedication) {
      const matches = medications.filter(m => 
        m.name.toLowerCase().includes(value.toLowerCase())
      );
      setNameSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else if (value.length === 0 && !editingMedication) {
      // Mostrar todos cuando el campo está vacío y tiene foco
      setNameSuggestions(medications);
      setShowSuggestions(true);
    } else {
      setNameSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Seleccionar medicamento existente para agregar stock
  // allowPriceChange: permite modificar el precio para calcular promedio ponderado
  const selectExistingMedication = (medication, allowPriceChange = false) => {
    setSelectedExisting({...medication, allowPriceChange});
    setFormData({
      name: medication.name,
      presentation: medication.presentation || '',
      presentation_quantity: medication.presentation_quantity || 0,
      category: medication.category || '',
      administration_type: medication.administration_type || '',
      initial_quantity: 0, // Solo la cantidad nueva
      min_stock: medication.min_stock || 0,
      unit: medication.unit || '',
      currency: medication.currency || '',
      supplier_id: medication.supplier_id || '',
      purchase_price: medication.purchase_price || 0, // Precio actual, puede cambiarse
      barcode: medication.barcode || '',
      lot_number: '', // Nuevo lote
      expiration_date: '', // Nueva fecha de vencimiento
      entry_date: ''
    });
    setShowSuggestions(false);
    setNameSuggestions([]);
    setBarcodeSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.presentation) {
      toast.error('Selecciona la presentación');
      return;
    }
    if (!formData.unit) {
      toast.error('Selecciona la unidad de medida');
      return;
    }
    if (!formData.currency) {
      toast.error('Selecciona la moneda');
      return;
    }
    if (!formData.category) {
      toast.error('Selecciona la categoría');
      return;
    }
    
    try {
      if (editingMedication) {
        // Editando medicamento existente.
        // Solo enviar current_stock si el usuario efectivamente lo modificó desde
        // el valor original mostrado al abrir el diálogo. Esto evita sobreescribir
        // cambios concurrentes de stock (despachos, movimientos) con el snapshot
        // que quedó pre-cargado en el formulario.
        const { initial_quantity, ...rest } = formData;
        const originalStock = editingMedication.current_stock || 0;
        const editedStock = parseFloat(initial_quantity) || 0;
        const stockChanged = Math.abs(editedStock - originalStock) > 0.0001;
        const editPayload = stockChanged
          ? { ...rest, current_stock: editedStock }
          : { ...rest };
        await medicationsAPI.update(editingMedication.id, editPayload);
        toast.success('Medicamento actualizado');
      } else if (selectedExisting) {
        // Agregando stock a medicamento existente
        if (formData.initial_quantity <= 0) {
          toast.error('Ingresa la cantidad a agregar');
          return;
        }
        
        // Verificar si el precio cambió para calcular promedio ponderado
        const priceChanged = formData.purchase_price !== selectedExisting.purchase_price;
        
        // Crear movimiento de entrada con el nuevo precio
        await medicationsAPI.createMovement({
          medication_id: selectedExisting.id,
          type: 'entrada',
          quantity: formData.initial_quantity,
          unit_cost: formData.purchase_price,
          currency: formData.currency,
          notes: `Entrada${formData.expiration_date ? `, Vence: ${formData.expiration_date}` : ''}${priceChanged ? ` | Precio: ${formData.purchase_price}` : ''}`
        });
        
        // Si el precio cambió, calcular promedio ponderado y actualizar
        if (priceChanged) {
          const currentStock = selectedExisting.current_stock || 0;
          const currentPrice = selectedExisting.purchase_price || 0;
          const newQuantity = formData.initial_quantity;
          const newPrice = formData.purchase_price;
          
          // Promedio ponderado: (stockActual * precioActual + cantidadNueva * precioNuevo) / (stockActual + cantidadNueva)
          const totalStock = currentStock + newQuantity;
          const weightedAveragePrice = totalStock > 0 
            ? ((currentStock * currentPrice) + (newQuantity * newPrice)) / totalStock
            : newPrice;
          
          await medicationsAPI.update(selectedExisting.id, {
            purchase_price: parseFloat(weightedAveragePrice.toFixed(2)),
            expiration_date: formData.expiration_date || selectedExisting.expiration_date
          });
          toast.success(`Stock agregado: +${formData.initial_quantity}. Precio promedio ponderado: ${weightedAveragePrice.toFixed(2)}`);
        } else {
          // Actualizar solo fecha de vencimiento
          if (formData.expiration_date) {
            await medicationsAPI.update(selectedExisting.id, {
              expiration_date: formData.expiration_date || selectedExisting.expiration_date
            });
          }
          toast.success(`Stock agregado: +${formData.initial_quantity} ${formData.presentation}`);
        }
      } else {
        // Creando nuevo medicamento
        await medicationsAPI.create(formData);
        toast.success('Medicamento creado');
      }
      resetForm();
      setMedicationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (medication) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'edit', data: medication });
      setSuperUserDialogOpen(true);
      return;
    }
    executeEdit(medication);
  };

  const executeEdit = (medication) => {
    setEditingMedication(medication);
    setFormData({
      name: medication.name,
      presentation: medication.presentation || '',
      presentation_quantity: medication.presentation_quantity || 0,
      category: medication.category || '',
      administration_type: medication.administration_type || '',
      initial_quantity: medication.current_stock,
      min_stock: medication.min_stock,
      unit: medication.unit || '',
      currency: medication.currency || '',
      supplier_id: medication.supplier_id || '',
      purchase_price: medication.purchase_price,
      barcode: medication.barcode || '',
      lot_number: medication.lot_number || '',
      expiration_date: medication.expiration_date || ''
    });
    setMedicationDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'delete', data: id });
      setSuperUserDialogOpen(true);
      return;
    }
    executeDelete(id);
  };

  const executeDelete = async (id) => {
    try {
      await medicationsAPI.delete(id);
      toast.success('Medicamento eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Handler para cuando el super usuario se verifica
  const handleSuperUserVerified = () => {
    if (!pendingAction) return;
    
    switch (pendingAction.type) {
      case 'edit':
        executeEdit(pendingAction.data);
        break;
      case 'delete':
        executeDelete(pendingAction.data);
        break;
      default:
        break;
    }
    setPendingAction(null);
  };

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    try {
      // Incluir información adicional en las notas
      const notesWithDetails = [
        movementForm.notes,
        movementForm.supplier_id ? `Proveedor: ${suppliers.find(s => s.id === movementForm.supplier_id)?.name || 'N/A'}` : '',
        movementForm.entry_date ? `Fecha entrada: ${movementForm.entry_date}` : '',
        `Moneda: ${movementForm.currency}`
      ].filter(Boolean).join(' | ');
      
      await medicationsAPI.createMovement({
        ...movementForm,
        notes: notesWithDetails
      });
      toast.success(`${movementForm.type === 'entrada' ? 'Entrada' : 'Salida'} registrada`);
      setMovementForm({ 
        medication_id: '', 
        type: 'entrada', 
        quantity: 0, 
        unit_cost: 0, 
        currency: 'DOP',
        supplier_id: '',
        entry_date: '',
        notes: '' 
      });
      setMovementDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  // Abrir diálogo de movimiento con medicamento pre-seleccionado
  const openMovementDialog = (medication) => {
    setMovementForm({
      medication_id: medication.id,
      type: 'entrada',
      quantity: 0,
      unit_cost: medication.purchase_price || 0,
      currency: medication.currency || 'DOP',
      supplier_id: medication.supplier_id || '',
      entry_date: '',
      notes: ''
    });
    setMovementDialogOpen(true);
  };

  const filteredMedications = medications.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular valor del inventario en ambas monedas
  const totalInventoryValueDOP = medications.reduce((sum, m) => {
    return sum + (m.current_stock * (m.price_dop || 0));
  }, 0);
  
  const totalInventoryValueUSD = medications.reduce((sum, m) => {
    return sum + (m.current_stock * (m.price_usd || 0));
  }, 0);

  const lowStockCount = medications.filter(m => m.current_stock <= m.min_stock).length;
  const expiringCount = medications.filter(m => {
    if (!m.expiration_date) return false;
    const expDate = new Date(m.expiration_date);
    const today = new Date();
    const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Medicamentos</h1>
          <p className="text-slate-500 mt-1">Inventario veterinario</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <MedicationMovementsSheet
            movements={movements}
            medications={medications}
          />
          <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-movement-btn" onClick={() => setMovementForm({ 
                medication_id: '', 
                type: 'entrada', 
                quantity: 0, 
                unit_cost: 0, 
                currency: 'DOP',
                supplier_id: '',
                entry_date: '',
                notes: '' 
              })}>
                <ArrowDownCircle className="h-4 w-4 mr-2" />
                Movimiento
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Registrar Movimiento
                  {movementForm.medication_id && (
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      - {medications.find(m => m.id === movementForm.medication_id)?.name}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateMovement} className="space-y-4">
                <div className="space-y-2">
                  <Label>Medicamento</Label>
                  <Select 
                    value={movementForm.medication_id} 
                    onValueChange={(v) => setMovementForm({...movementForm, medication_id: v})}
                  >
                    <SelectTrigger data-testid="movement-medication-select">
                      <SelectValue placeholder="Seleccionar medicamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {medications.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select 
                      value={movementForm.type} 
                      onValueChange={(v) => setMovementForm({...movementForm, type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entrada">Entrada (Compra)</SelectItem>
                        <SelectItem value="salida">Salida (Uso)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Cantidad
                      {movementForm.medication_id && (() => {
                        const med = medications.find(m => m.id === movementForm.medication_id);
                        if (med?.presentation) {
                          const extra = (med.presentation.toLowerCase().includes('saco') && med.presentation_quantity > 0)
                            ? ` (cada ${med.presentation} = ${med.presentation_quantity} ${med.unit})`
                            : '';
                          return <span className="text-xs text-slate-500 ml-1">en {med.presentation}{extra}</span>;
                        }
                        return null;
                      })()}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={movementForm.quantity}
                      onChange={(e) => setMovementForm({...movementForm, quantity: parseFloat(e.target.value) || 0})}
                      required
                    />
                    {movementForm.medication_id && movementForm.quantity > 0 && (() => {
                      const med = medications.find(m => m.id === movementForm.medication_id);
                      if (med?.presentation?.toLowerCase().includes('saco') && med.presentation_quantity > 0) {
                        const totalLb = movementForm.quantity * med.presentation_quantity;
                        return (
                          <p className="text-xs text-blue-600">
                            = {totalLb.toLocaleString('es-DO', {maximumFractionDigits: 2})} {med.unit} totales
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
                {movementForm.type === 'entrada' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Costo Unitario</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={movementForm.unit_cost}
                          onChange={(e) => setMovementForm({...movementForm, unit_cost: parseFloat(e.target.value) || 0})}
                        />
                        <p className="text-xs text-slate-500">Precio por unidad (no el total).</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Moneda</Label>
                        <Select 
                          value={movementForm.currency} 
                          onValueChange={(v) => setMovementForm({...movementForm, currency: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DOP">Peso Dominicano (RD$)</SelectItem>
                            <SelectItem value="USD">Dólar (US$)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* Total factura en vivo */}
                    {(movementForm.unit_cost > 0 && movementForm.quantity > 0) && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2" data-testid="movement-total-invoice-preview">
                        <p className="text-sm text-blue-700">
                          Total Factura: <strong className="text-blue-900">
                            {formatCurrency(movementForm.unit_cost * movementForm.quantity, movementForm.currency)}
                          </strong>
                          <span className="text-blue-500 ml-1 text-xs">
                            ({formatStock(movementForm.quantity)} × {movementForm.unit_cost})
                          </span>
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Proveedor</Label>
                        <Select 
                          value={movementForm.supplier_id} 
                          onValueChange={(v) => setMovementForm({...movementForm, supplier_id: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar proveedor..." />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha de Entrada</Label>
                        <Input
                          type="date"
                          value={movementForm.entry_date}
                          onChange={(e) => setMovementForm({...movementForm, entry_date: e.target.value})}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>Notas (opcional)</Label>
                  <Input
                    value={movementForm.notes}
                    onChange={(e) => setMovementForm({...movementForm, notes: e.target.value})}
                    placeholder="Ej: Lote ABC-123"
                  />
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline">Cancelar</Button>
                  </DialogClose>
                  <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">
                    Registrar
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button 
            className="bg-[#931A25] hover:bg-[#7A151F]" 
            data-testid="add-medication-btn"
            onClick={() => setWizardOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Medicamento
          </Button>

          <Dialog open={medicationDialogOpen} onOpenChange={(open) => {
            setMedicationDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingMedication ? 'Editar Medicamento' : 
                   selectedExisting ? `Agregar Stock: ${selectedExisting.name}` : 
                   'Nuevo Medicamento'}
                </DialogTitle>
              </DialogHeader>
              
              {/* Banner cuando se selecciona medicamento existente */}
              {selectedExisting && !editingMedication && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                  <p className="text-sm text-blue-800">
                    <strong>Agregando stock a medicamento existente.</strong> Solo necesitas ingresar la cantidad, fecha de vencimiento y fecha de entrada.
                  </p>
                  <Button 
                    type="button" 
                    variant="link" 
                    className="text-blue-600 p-0 h-auto text-sm"
                    onClick={() => {
                      setSelectedExisting(null);
                      setFormData({...formData, name: ''});
                    }}
                  >
                    Crear nuevo medicamento en su lugar
                  </Button>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Búsqueda por código de barras */}
                {!editingMedication && !selectedExisting && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <Label className="text-blue-800 flex items-center gap-2 mb-2">
                      <ScanBarcode className="h-4 w-4" />
                      Buscar por Código de Barras
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={barcodeSearch}
                        onChange={(e) => setBarcodeSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleBarcodeSearch(barcodeSearch);
                          }
                        }}
                        placeholder="Escanea o ingresa el código..."
                        className="flex-1"
                        data-testid="barcode-search-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleBarcodeSearch(barcodeSearch)}
                        disabled={searchingBarcode || barcodeSearch.length < 3}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        {searchingBarcode ? (
                          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Si el código existe, se cargará el medicamento. Si no, podrás crear uno nuevo.</p>
                  </div>
                )}
                
                {/* Tip para búsqueda por nombre */}
                {!editingMedication && !selectedExisting && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      <strong>Tip:</strong> También puedes buscar por nombre para encontrar medicamentos existentes.
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label>Nombre * {!editingMedication && !selectedExisting && <span className="text-xs text-slate-400">(busca o crea nuevo)</span>}</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      onFocus={() => {
                        if (!editingMedication && !selectedExisting) {
                          if (formData.name.length === 0) {
                            setNameSuggestions(medications);
                            setShowSuggestions(medications.length > 0);
                          } else if (formData.name.length >= 1) {
                            const matches = medications.filter(m => 
                              m.name.toLowerCase().includes(formData.name.toLowerCase())
                            );
                            setNameSuggestions(matches);
                            setShowSuggestions(matches.length > 0);
                          }
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Buscar o escribir nombre..."
                      required
                      disabled={!!selectedExisting}
                      data-testid="medication-name-input"
                      className={!editingMedication && !selectedExisting ? "pr-8" : ""}
                    />
                    {!editingMedication && !selectedExisting && (
                      <Search className="absolute right-3 top-9 h-4 w-4 text-slate-400 pointer-events-none" />
                    )}
                    {/* Dropdown de sugerencias mejorado */}
                    {showSuggestions && nameSuggestions.length > 0 && !editingMedication && !selectedExisting && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-amber-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        <div className="p-2 text-xs font-medium text-amber-700 border-b bg-amber-50 sticky top-0">
                          {formData.name ? `${nameSuggestions.length} medicamento(s) encontrado(s)` : 'Selecciona un medicamento existente:'}
                        </div>
                        {nameSuggestions.map((med) => (
                          <div
                            key={med.id}
                            className="px-3 py-2.5 hover:bg-amber-50 cursor-pointer border-b last:border-0 transition-colors"
                            onMouseDown={() => selectExistingMedication(med)}
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm text-slate-800">{med.name}</p>
                              <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">Stock: {formatStock(med.current_stock)}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {med.category} • {med.presentation || 'Sin presentación'} {med.presentation_quantity ? `(${med.presentation_quantity} ${med.unit})` : ''}
                            </p>
                          </div>
                        ))}
                        {formData.name && (
                          <div 
                            className="px-3 py-2 bg-green-50 hover:bg-green-100 cursor-pointer border-t-2 border-green-200 transition-colors"
                            onMouseDown={() => {
                              setShowSuggestions(false);
                              setNameSuggestions([]);
                            }}
                          >
                            <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              Crear nuevo: "{formData.name}"
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Categoría *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({...formData, category: v})}
                      disabled={!!selectedExisting}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar categoría..." />
                      </SelectTrigger>
                      <SelectContent>
                        {MEDICATION_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Tipo de Administración y Fecha de Entrada */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Administración</Label>
                    <Select 
                      value={formData.administration_type} 
                      onValueChange={(v) => setFormData({...formData, administration_type: v})}
                      disabled={!!selectedExisting}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ADMINISTRATION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Entrada *</Label>
                    <Input
                      type="date"
                      value={formData.entry_date}
                      onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
                      required
                    />
                  </div>
                </div>

                {/* Presentación con cantidad */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Presentación *</Label>
                    <Select 
                      value={formData.presentation} 
                      onValueChange={(v) => setFormData({...formData, presentation: v})}
                      disabled={!!selectedExisting}
                    >
                      <SelectTrigger data-testid="medication-presentation-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {MEDICATION_PRESENTATIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Contenido *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.presentation_quantity}
                      onChange={(e) => setFormData({...formData, presentation_quantity: parseFloat(e.target.value) || 0})}
                      placeholder="Ej: 50"
                      onFocus={(e) => e.target.select()}
                      disabled={!!selectedExisting}
                      data-testid="medication-presentation-qty-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unidad de Medida *</Label>
                    <Select 
                      value={formData.unit} 
                      onValueChange={(v) => setFormData({...formData, unit: v})}
                      disabled={!!selectedExisting}
                    >
                      <SelectTrigger data-testid="medication-unit-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {MEDICATION_UNITS.map((u) => (
                          <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.presentation && formData.presentation_quantity > 0 && formData.unit && (
                  <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                    Cada <span className="font-medium capitalize">{MEDICATION_PRESENTATIONS.find(p => p.value === formData.presentation)?.label || formData.presentation}</span> contiene <span className="font-medium">{formData.presentation_quantity} {formData.unit}</span>
                  </p>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div className={`space-y-2 ${selectedExisting ? 'bg-green-50 p-2 rounded-lg border-2 border-green-300' : ''}`}>
                    <Label>{selectedExisting ? 'Cantidad a Agregar *' : 'Cantidad *'}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.initial_quantity}
                      onChange={(e) => setFormData({...formData, initial_quantity: parseFloat(e.target.value) || 0})}
                      placeholder={selectedExisting ? "Ingresa cantidad nueva" : "Cantidad a agregar"}
                      required
                      className={selectedExisting ? 'border-green-400 focus:border-green-500' : ''}
                      data-testid="medication-quantity-input"
                    />
                    {selectedExisting && (
                      <p className="text-xs text-green-600">Stock actual: {formatStock(selectedExisting.current_stock)} {selectedExisting.presentation}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Stock Mínimo</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({...formData, min_stock: parseFloat(e.target.value) || 0})}
                      disabled={!!selectedExisting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Código de Barras</Label>
                    <Input
                      value={formData.barcode}
                      onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                      placeholder="Escanear o ingresar código"
                      disabled={!!selectedExisting}
                      data-testid="medication-barcode-input"
                    />
                  </div>
                </div>

                {/* Proveedor y Moneda */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Proveedor</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={formData.supplier_id} 
                        onValueChange={(v) => setFormData({...formData, supplier_id: v})}
                      >
                        <SelectTrigger className="flex-1" data-testid="medication-supplier-select">
                          <SelectValue placeholder="Seleccionar proveedor" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setNewSupplierDialogOpen(true)}
                        className="flex-shrink-0"
                        data-testid="add-supplier-quick-btn"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Moneda *</Label>
                    <Select 
                      value={formData.currency} 
                      onValueChange={(v) => setFormData({...formData, currency: v})}
                      disabled={!!selectedExisting}
                    >
                      <SelectTrigger data-testid="medication-currency-select">
                        <SelectValue placeholder="Seleccionar moneda..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DOP">Peso Dominicano (RD$)</SelectItem>
                        <SelectItem value="USD">Dólar (US$)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Precio Unitario ({formData.currency === 'USD' ? 'US$' : 'RD$'}) *
                      {selectedExisting && <span className="text-xs text-green-600 ml-1">(editable)</span>}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({...formData, purchase_price: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      required
                      placeholder="0.00"
                      data-testid="medication-price-input"
                      className={selectedExisting && formData.purchase_price !== selectedExisting.purchase_price ? "border-green-400 bg-green-50" : ""}
                    />
                    {/* Total factura calculado en vivo */}
                    {(parseFloat(formData.purchase_price) > 0 && parseFloat(formData.initial_quantity) > 0) && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2" data-testid="total-invoice-preview">
                        <p className="text-xs text-blue-700">
                          Total Factura: <strong className="text-blue-900">
                            {formatCurrency(
                              parseFloat(formData.purchase_price) * parseFloat(formData.initial_quantity),
                              formData.currency || 'DOP'
                            )}
                          </strong>
                          <span className="text-blue-500 ml-1">
                            ({formatStock(formData.initial_quantity)} × {formData.purchase_price})
                          </span>
                        </p>
                      </div>
                    )}
                    {selectedExisting ? (
                      <div className="text-xs space-y-1">
                        <p className="text-slate-500">Precio unitario actual: {selectedExisting.purchase_price} {selectedExisting.currency}</p>
                        {formData.purchase_price !== selectedExisting.purchase_price && formData.initial_quantity > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded p-2">
                            <p className="text-green-700 font-medium">Se calculará promedio ponderado:</p>
                            <p className="text-green-600">
                              ({formatStock(selectedExisting.current_stock)} × {selectedExisting.purchase_price} + {formatStock(formData.initial_quantity)} × {formData.purchase_price}) / {formatStock(selectedExisting.current_stock + formData.initial_quantity)} = 
                              <strong className="ml-1">
                                {(((selectedExisting.current_stock * selectedExisting.purchase_price) + (formData.initial_quantity * formData.purchase_price)) / (selectedExisting.current_stock + formData.initial_quantity)).toFixed(2)}
                              </strong>
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        Ingresa el precio POR UNIDAD (no el total). El total de la factura se calculará automáticamente.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Vencimiento</Label>
                    <Input
                      type="date"
                      value={formData.expiration_date}
                      onChange={(e) => setFormData({...formData, expiration_date: e.target.value})}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
                  </DialogClose>
                  <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-medication-btn">
                    {editingMedication ? 'Actualizar' : selectedExisting ? 'Agregar Stock' : 'Guardar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 gap-4 ${canSeeMedAmounts ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#931A25]/10 flex items-center justify-center">
              <Pill className="h-6 w-6 text-[#931A25]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Medicamentos</p>
              <p className="text-xl font-bold text-slate-900">{medications.length}</p>
            </div>
          </CardContent>
        </Card>
        {canSeeMedAmounts && (
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <ArrowUpCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Valor Inventario</p>
              <p className="text-lg font-bold text-slate-900">{formatCurrency(totalInventoryValueUSD, 'USD')}</p>
              <p className="text-sm font-medium text-blue-600">{formatCurrency(totalInventoryValueDOP, 'DOP')}</p>
            </div>
          </CardContent>
        </Card>
        )}
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${lowStockCount > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              <AlertTriangle className={`h-6 w-6 ${lowStockCount > 0 ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Stock Bajo</p>
              <p className="text-xl font-bold text-slate-900">{lowStockCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${expiringCount > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
              <Calendar className={`h-6 w-6 ${expiringCount > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Por Vencer (30d)</p>
              <p className="text-xl font-bold text-slate-900">{expiringCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Buscar por nombre o categoría..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-medication-input"
        />
      </div>

      {/* Medications Inventory — Opción B: Tabla Moderna */}
      <InventoryModernTableView medications={filteredMedications} onMovement={openMovementDialog} onEdit={handleEdit} onDelete={handleDelete} isSuperUser={isSuperUser} />

      {/* Recent Movements -> ahora se muestra en el panel deslizante desde el header */}

      {/* Recent Movements -> ahora se muestra en el panel deslizante desde el header */}

      {/* Quick Add Supplier Dialog */}
      <Dialog open={newSupplierDialogOpen} onOpenChange={setNewSupplierDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Nuevo Proveedor
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSupplier} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={newSupplierForm.name}
                onChange={(e) => setNewSupplierForm({...newSupplierForm, name: e.target.value})}
                placeholder="Nombre de la empresa"
                required
                data-testid="quick-supplier-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>RNC</Label>
                <Input
                  value={newSupplierForm.rnc}
                  onChange={(e) => setNewSupplierForm({...newSupplierForm, rnc: e.target.value})}
                  placeholder="000-00000-0"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={newSupplierForm.phone}
                  onChange={(e) => setNewSupplierForm({...newSupplierForm, phone: e.target.value})}
                  placeholder="809-000-0000"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newSupplierForm.email}
                onChange={(e) => setNewSupplierForm({...newSupplierForm, email: e.target.value})}
                placeholder="correo@empresa.com"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancelar</Button>
              </DialogClose>
              <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]">
                Guardar Proveedor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Super User Verification Dialog */}
      <SuperUserDialog
        open={superUserDialogOpen}
        onOpenChange={setSuperUserDialogOpen}
        onSuccess={handleSuperUserVerified}
        title="Acción Restringida"
        description="Esta acción requiere verificación de super usuario."
      />

      {/* Wizard: Nuevo Medicamento / Agregar Stock */}
      <AddMedicationWizard 
        open={wizardOpen} 
        onOpenChange={setWizardOpen} 
        medications={medications} 
        suppliers={suppliers} 
        onSaved={fetchData} 
      />
    </div>
  );
}
